import { useRef, useState } from 'react';
import {
  AspectRatio,
  Box,
  Button,
  HStack,
  IconButton,
  Image,
  SimpleGrid,
  Spinner,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import {
  AddIcon,
  ArrowBackIcon,
  ArrowForwardIcon,
  CloseIcon,
} from '@chakra-ui/icons';
import { errorMessage } from './estimateApi';

const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;
// The PDF fits 4 photos per page and adds pages as needed.
export const PHOTOS_PER_PAGE = 4;
// The server takes at most this many files per upload request.
const UPLOAD_BATCH = 10;

/**
 * Photos for the estimate's photo pages, in page order. Every added photo
 * goes in the PDF; the arrows reorder them and deleting takes one out. The cover photo is managed separately (EstimateCoverPhoto) and isn't
 * shown here. Every change is saved immediately.
 *
 * `state` is { photos: [{key, name, url}], order: [key], coverKey }.
 */
const EstimatePhotos = ({ api, state, onChange, isDisabled, toast }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const { order } = state;
  const pagePhotos = order
    .map(key => state.photos.find(photo => photo.key === key))
    .filter(Boolean);
  const locked = isDisabled || saving || uploading;

  const fail = (title, error) =>
    toast({
      title,
      description: errorMessage(error),
      status: 'error',
      duration: 5000,
      isClosable: true,
    });

  const move = async (index, step) => {
    const next = [...order];
    [next[index], next[index + step]] = [next[index + step], next[index]];
    const previous = state;
    onChange({ ...state, order: next });
    setSaving(true);
    try {
      onChange(await api.post('/api/pdf/photos/arrange', { order: next }));
    } catch (error) {
      onChange(previous);
      fail('Could not save photo order', error);
    }
    setSaving(false);
  };

  const remove = async key => {
    setSaving(true);
    try {
      onChange(await api.post('/api/pdf/photos/remove', { key }));
    } catch (error) {
      fail('Could not remove photo', error);
    }
    setSaving(false);
  };

  const upload = async e => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;

    const invalid = files.find(
      file => !file.type.startsWith('image/') || file.size > MAX_UPLOAD_BYTES
    );
    if (invalid) {
      toast({
        title: `"${invalid.name}" can't be added`,
        description: 'Photos must be images under 20 MB.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setUploading(true);
    try {
      for (let start = 0; start < files.length; start += UPLOAD_BATCH) {
        const form = new FormData();
        files
          .slice(start, start + UPLOAD_BATCH)
          .forEach(file => form.append('files', file));
        form.append('username', api.username);
        onChange(await api.post('/api/pdf/photos', form));
      }
    } catch (error) {
      fail('Upload failed', error);
    }
    setUploading(false);
  };

  const pageCount = Math.ceil(pagePhotos.length / PHOTOS_PER_PAGE);

  return (
    <VStack align="stretch" spacing={3}>
      <HStack justify="space-between">
        <Text fontSize="xs" color="gray.500">
          {pagePhotos.length === 0
            ? 'No photos yet'
            : `${pagePhotos.length} photo${
                pagePhotos.length === 1 ? '' : 's'
              } · ${pageCount} page${pageCount === 1 ? '' : 's'}`}
          {saving && <Spinner size="xs" ml={2} />}
        </Text>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          onChange={upload}
          style={{ display: 'none' }}
        />
        <Button
          size="xs"
          variant="outline"
          leftIcon={<AddIcon boxSize={2} />}
          onClick={() => inputRef.current.click()}
          isLoading={uploading}
          isDisabled={isDisabled}
        >
          Add Photos
        </Button>
      </HStack>

      {pagePhotos.length > 0 && (
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
          {pagePhotos.map((photo, index) => (
            <Box key={photo.key}>
              <AspectRatio ratio={1}>
                <Box
                  position="relative"
                  borderRadius="md"
                  overflow="hidden"
                  borderWidth="1px"
                  borderColor="gray.200"
                >
                  {photo.url ? (
                    <Image
                      src={photo.url}
                      alt=""
                      w="100%"
                      h="100%"
                      objectFit="cover"
                    />
                  ) : (
                    <Text fontSize="xs">{photo.name}</Text>
                  )}
                  <Box
                    position="absolute"
                    top={1}
                    left={1}
                    px={2}
                    py="1px"
                    borderRadius="full"
                    bg="blue.500"
                    color="white"
                    fontSize="xs"
                    fontWeight="bold"
                  >
                    {index + 1}
                  </Box>
                </Box>
              </AspectRatio>
              <HStack justify="space-between" mt={1}>
                <HStack spacing={0}>
                  <IconButton
                    size="xs"
                    variant="ghost"
                    icon={<ArrowBackIcon />}
                    aria-label={`Move ${photo.name} earlier`}
                    onClick={() => move(index, -1)}
                    isDisabled={locked || index === 0}
                  />
                  <IconButton
                    size="xs"
                    variant="ghost"
                    icon={<ArrowForwardIcon />}
                    aria-label={`Move ${photo.name} later`}
                    onClick={() => move(index, 1)}
                    isDisabled={locked || index === pagePhotos.length - 1}
                  />
                </HStack>
                <Tooltip label="Delete photo">
                  <IconButton
                    size="xs"
                    variant="ghost"
                    colorScheme="red"
                    icon={<CloseIcon boxSize={2} />}
                    aria-label={`Delete ${photo.name}`}
                    onClick={() => remove(photo.key)}
                    isDisabled={locked}
                  />
                </Tooltip>
              </HStack>
            </Box>
          ))}
        </SimpleGrid>
      )}
    </VStack>
  );
};

export default EstimatePhotos;
